if (!customElements.get("m-gmaps")) {
  class MGmaps extends HTMLElement {
    constructor() {
      super();

      this.mapElm = this.querySelector("#m-gmaps-inner");
      if (!this.mapElm) return;

      this.section = this.closest('[data-section-type="google-maps"]');

      this.mapAddress = this.mapElm.getAttribute("data-map-title") || "";
      this.mapX = this.mapElm.getAttribute("data-map-x") || "";
      this.mapY = this.mapElm.getAttribute("data-map-y") || "";

      this.mapAddress2 =
        this.mapElm.getAttribute("data-map-title-2") || "";
      this.mapX2 =
        this.mapElm.getAttribute("data-map-x-2") || "";
      this.mapY2 =
        this.mapElm.getAttribute("data-map-y-2") || "";

      this.mapZoom =
        parseInt(this.mapElm.getAttribute("data-map-zoom"), 10) || 13;

      this.key = this.mapElm.getAttribute("data-key") || "";

      this.slides = [];
      this.dots = [];
      this.markers = [];
      this.infoWindows = [];
      this.activeIndex = 0;
    }

    connectedCallback() {
      if (!this.mapElm || !this.key) return;

      this.slides = Array.from(
        this.section.querySelectorAll("[data-map-slide]")
      );

      this.dots = Array.from(
        this.section.querySelectorAll("[data-map-dot]")
      );

      this.prevButton =
        this.section.querySelector("[data-map-prev]");

      this.nextButton =
        this.section.querySelector("[data-map-next]");

      this.setupSlider();
      this.loadGoogleMaps();
    }

    setupSlider() {
      this.showSlide(0, false);

      if (this.prevButton) {
        this.prevButton.addEventListener("click", () => {
          const newIndex =
            this.activeIndex <= 0
              ? this.slides.length - 1
              : this.activeIndex - 1;

          this.showSlide(newIndex, true);
        });
      }

      if (this.nextButton) {
        this.nextButton.addEventListener("click", () => {
          const newIndex =
            this.activeIndex >= this.slides.length - 1
              ? 0
              : this.activeIndex + 1;

          this.showSlide(newIndex, true);
        });
      }

      this.dots.forEach((dot, index) => {
        dot.addEventListener("click", () => {
          this.showSlide(index, true);
        });
      });
    }

    showSlide(index, moveMap = true) {
      if (!this.slides.length) return;

      if (index < 0) index = this.slides.length - 1;
      if (index >= this.slides.length) index = 0;

      this.activeIndex = index;

      this.slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === index;

        slide.classList.toggle("is-active", isActive);
        slide.hidden = !isActive;
        slide.setAttribute(
          "aria-hidden",
          isActive ? "false" : "true"
        );
      });

      this.dots.forEach((dot, dotIndex) => {
        dot.classList.toggle("is-active", dotIndex === index);
      });

      if (moveMap && this.map && this.markers[index]) {
        this.closeInfoWindows();

        const marker = this.markers[index];
        const position = marker.getPosition();

        this.map.panTo(position);
        this.map.setZoom(this.mapZoom);

        if (this.infoWindows[index]) {
          this.infoWindows[index].open({
            map: this.map,
            anchor: marker
          });
        }
      }
    }

    loadGoogleMaps() {
      this.loadScript()
        .then(() => this.initMap())
        .catch((error) => {
          console.error("Google Maps Fehler:", error);
        });
    }

    loadScript() {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve();
          return;
        }

        const existingScript = document.querySelector(
          'script[src*="maps.googleapis.com/maps/api/js"]'
        );

        if (existingScript) {
          if (window.google && window.google.maps) {
            resolve();
          } else {
            existingScript.addEventListener("load", resolve, {
              once: true
            });

            existingScript.addEventListener("error", reject, {
              once: true
            });
          }

          return;
        }

        const script = document.createElement("script");

        script.src =
          "https://maps.googleapis.com/maps/api/js?key=" +
          encodeURIComponent(this.key);

        script.async = true;
        script.defer = true;
        script.onload = resolve;
        script.onerror = reject;

        document.head.appendChild(script);
      });
    }

    validCoordinate(value) {
      return (
        value !== null &&
        value !== undefined &&
        String(value).trim() !== "" &&
        !Number.isNaN(parseFloat(value))
      );
    }

    markerIcon() {
      return {
        url:
          "data:image/svg+xml;charset=UTF-8," +
          encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="45" viewBox="0 0 32 45">
              <path fill="#DB899D" d="M16 0C7.16 0 0 7.16 0 16c0 12 16 29 16 29s16-17 16-29C32 7.16 24.84 0 16 0z"/>
              <circle fill="#ffffff" cx="16" cy="16" r="6"/>
            </svg>
          `),
        scaledSize: new google.maps.Size(28, 40),
        anchor: new google.maps.Point(14, 40)
      };
    }

    closeInfoWindows() {
      this.infoWindows.forEach((infoWindow) => {
        infoWindow.close();
      });
    }

    createMarker(position, title, x, y, index) {
      const marker = new google.maps.Marker({
        map: this.map,
        position: position,
        title: title,
        icon: this.markerIcon()
      });

      const mapsUrl =
        "https://www.google.com/maps/search/?api=1&query=" +
        encodeURIComponent(`${x},${y}`);

      const infoWindow = new google.maps.InfoWindow({
        content: `
          <div class="m-map-info">
            <strong>${title}</strong><br>
            <a href="${mapsUrl}" target="_blank" rel="noopener">
              In Google Maps öffnen
            </a>
          </div>
        `
      });

      marker.addListener("click", () => {
        this.closeInfoWindows();

        infoWindow.open({
          map: this.map,
          anchor: marker
        });

        this.showSlide(index, false);
      });

      this.markers[index] = marker;
      this.infoWindows[index] = infoWindow;

      return marker;
    }

    initMap() {
      if (
        !this.validCoordinate(this.mapX) ||
        !this.validCoordinate(this.mapY)
      ) {
        console.error("Erster Google-Maps-Standort ist ungültig.");
        return;
      }

      const position1 = new google.maps.LatLng(
        parseFloat(this.mapX),
        parseFloat(this.mapY)
      );

      const hasSecondLocation =
        this.validCoordinate(this.mapX2) &&
        this.validCoordinate(this.mapY2);

      const mapOptions = {
        zoom: this.mapZoom,
        center: position1,
        draggable: true,
        gestureHandling: "greedy",
        clickableIcons: false,
        scrollwheel: false,
        disableDoubleClickZoom: true,
        disableDefaultUI: true,
        styles: [
          {
            featureType: "water",
            elementType: "geometry",
            stylers: [
              { color: "#e9e9e9" },
              { lightness: 17 }
            ]
          },
          {
            featureType: "landscape",
            elementType: "geometry",
            stylers: [
              { color: "#f5f5f5" },
              { lightness: 20 }
            ]
          },
          {
            featureType: "road.highway",
            elementType: "geometry.fill",
            stylers: [
              { color: "#ffffff" },
              { lightness: 17 }
            ]
          },
          {
            featureType: "road.highway",
            elementType: "geometry.stroke",
            stylers: [
              { color: "#ffffff" },
              { lightness: 29 },
              { weight: 0.2 }
            ]
          },
          {
            featureType: "road.arterial",
            elementType: "geometry",
            stylers: [
              { color: "#ffffff" },
              { lightness: 18 }
            ]
          },
          {
            featureType: "road.local",
            elementType: "geometry",
            stylers: [
              { color: "#ffffff" },
              { lightness: 16 }
            ]
          },
          {
            featureType: "poi",
            elementType: "geometry",
            stylers: [
              { color: "#f5f5f5" },
              { lightness: 21 }
            ]
          },
          {
            featureType: "poi.park",
            elementType: "geometry",
            stylers: [
              { color: "#dedede" },
              { lightness: 21 }
            ]
          },
          {
            elementType: "labels.text.stroke",
            stylers: [
              { visibility: "on" },
              { color: "#ffffff" },
              { lightness: 16 }
            ]
          },
          {
            elementType: "labels.text.fill",
            stylers: [
              { saturation: 36 },
              { color: "#333333" },
              { lightness: 40 }
            ]
          },
          {
            elementType: "labels.icon",
            stylers: [{ visibility: "off" }]
          },
          {
            featureType: "transit",
            elementType: "geometry",
            stylers: [
              { color: "#f2f2f2" },
              { lightness: 19 }
            ]
          },
          {
            featureType: "administrative",
            elementType: "geometry.fill",
            stylers: [
              { color: "#fefefe" },
              { lightness: 20 }
            ]
          },
          {
            featureType: "administrative",
            elementType: "geometry.stroke",
            stylers: [
              { color: "#fefefe" },
              { lightness: 17 },
              { weight: 1.2 }
            ]
          }
        ]
      };

      this.map = new google.maps.Map(
        this.mapElm,
        mapOptions
      );

      const bounds = new google.maps.LatLngBounds();

      this.createMarker(
        position1,
        this.mapAddress,
        this.mapX,
        this.mapY,
        0
      );

      bounds.extend(position1);

      if (hasSecondLocation) {
        const position2 = new google.maps.LatLng(
          parseFloat(this.mapX2),
          parseFloat(this.mapY2)
        );

        this.createMarker(
          position2,
          this.mapAddress2,
          this.mapX2,
          this.mapY2,
          1
        );

        bounds.extend(position2);

        this.map.fitBounds(bounds, {
          top: 80,
          right: 80,
          bottom: 80,
          left: 80
        });

        google.maps.event.addListenerOnce(
          this.map,
          "idle",
          () => {
            const currentZoom = this.map.getZoom();

            if (currentZoom > this.mapZoom) {
              this.map.setZoom(this.mapZoom);
            }
          }
        );
      }

      window.addEventListener("resize", () => {
        google.maps.event.trigger(this.map, "resize");

        if (hasSecondLocation) {
          this.map.fitBounds(bounds, {
            top: 80,
            right: 80,
            bottom: 80,
            left: 80
          });
        }
      });
    }
  }

  customElements.define("m-gmaps", MGmaps);
}